import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    ReceiptEuro, 
    Search, 
    Filter, 
    Download, 
    ArrowUpDown, 
    ChevronLeft, 
    ChevronRight, 
    Building2, 
    Calendar,
    CheckCircle2,
    XCircle,
    Clock,
    Loader2,
    MoreHorizontal,
    FileText,
    ArrowUpRight,
    TrendingUp
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BillingLedger = () => {
    const [records, setRecords] = useState([]);
    const [summary, setSummary] = useState({ totalDue: 0, totalPaid: 0, outstanding: 0 });
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });

    useEffect(() => {
        fetchBillingRecords();
    }, [pagination.page, statusFilter]);

    const fetchBillingRecords = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/super-admin/billing', {
                params: {
                    status: statusFilter,
                    page: pagination.page,
                    institutionId: search
                }
            });
            setRecords(response.data.records);
            setSummary(response.data.summary);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching billing records:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-8 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Billing Ledger</h1>
                    <p className="text-slate-500 text-sm mt-1">Platform-wide subscription revenue tracking</p>
                </div>
                <div className="flex items-center gap-3">
                    <button className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-100">
                        <Download className="h-4 w-4" /> Download Statement
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-5">
                    <div className="p-4 bg-indigo-50 rounded-2xl">
                        <TrendingUp className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Generated Revenue</p>
                        <h3 className="text-xl font-black text-slate-900">KES {summary.totalDue?.toLocaleString()}</h3>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-5">
                    <div className="p-4 bg-green-50 rounded-2xl">
                        <CheckCircle2 className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Realized Revenue</p>
                        <h3 className="text-xl font-black text-slate-900">KES {summary.totalPaid?.toLocaleString()}</h3>
                    </div>
                </div>
                <div className="bg-slate-900 p-6 rounded-2xl shadow-xl flex items-center gap-5">
                    <div className="p-4 bg-slate-800 rounded-2xl">
                        <ArrowUpRight className="h-6 w-6 text-blue-400" />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Outstanding Platform Debt</p>
                        <h3 className="text-xl font-black text-white">KES {summary.outstanding?.toLocaleString()}</h3>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input 
                        type="text" 
                        placeholder="Search billing records..." 
                        className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50/50"
                    />
                </div>
                <div className="flex items-center gap-2 w-full md:w-auto">
                    <Filter className="h-4 w-4 text-slate-400" />
                    <select 
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="flex-1 md:w-48 px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50/50"
                    >
                        <option value="">All Payment Statuses</option>
                        <option value="paid">Paid</option>
                        <option value="pending">Pending</option>
                        <option value="overdue">Overdue</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100">
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Institution</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Billing Period</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Due Date</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Amount Due</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center">
                                        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
                                    </td>
                                </tr>
                            ) : records.map(record => (
                                <tr key={record.id} className="hover:bg-slate-50/30 transition-colors group">
                                    <td className="px-6 py-4">
                                        <Link to={`/super-admin/institutions/${record.institution_subscription?.institution?.id}`} className="flex items-center gap-3 group">
                                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-slate-400 text-xs">
                                                {record.institution_subscription?.institution?.name?.[0]}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-bold text-slate-900 truncate group-hover:text-blue-600 transition-colors">
                                                    {record.institution_subscription?.institution?.name}
                                                </p>
                                                <p className="text-[10px] text-slate-400 font-medium tracking-tight">#{record.transactionReference || 'REF-N/A'}</p>
                                            </div>
                                        </Link>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="h-4 w-4 text-slate-300" />
                                            <span className="text-sm font-bold text-slate-700">
                                                {new Date(record.billingPeriodStart).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-sm font-medium text-slate-500">
                                            {new Date(record.dueDate).toLocaleDateString()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <p className="text-sm font-black text-slate-900">KES {parseFloat(record.amountDue).toLocaleString()}</p>
                                        {record.amountPaid > 0 && (
                                            <p className="text-[10px] text-green-600 font-bold">Paid: {parseFloat(record.amountPaid).toLocaleString()}</p>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                            record.status === 'paid' ? 'bg-green-100 text-green-700' : 
                                            record.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                        }`}>
                                            {record.status === 'paid' ? <CheckCircle2 className="h-3 w-3" /> : record.status === 'overdue' ? <XCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                                            {record.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all" title="View Statement">
                                                <FileText className="h-4 w-4" />
                                            </button>
                                            <button className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all">
                                                <MoreHorizontal className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && records.length > 0 && (
                    <div className="px-6 py-6 border-t border-slate-50 flex items-center justify-between bg-slate-50/20">
                        <p className="text-xs font-bold text-slate-400">Page {pagination.page} of {pagination.pages}</p>
                        <div className="flex items-center gap-3">
                            <button 
                                disabled={pagination.page === 1}
                                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                                className="flex items-center gap-1 px-4 py-2 border border-slate-200 rounded-xl text-xs font-black text-slate-500 hover:text-slate-900 bg-white transition-all shadow-sm disabled:opacity-50"
                            >
                                <ChevronLeft className="h-4 w-4" /> Previous
                            </button>
                            <button 
                                disabled={pagination.page === pagination.pages}
                                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                                className="flex items-center gap-1 px-4 py-2 border border-slate-200 rounded-xl text-xs font-black text-slate-500 hover:text-slate-900 bg-white transition-all shadow-sm disabled:opacity-50"
                            >
                                Next <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BillingLedger;
